import React from 'react'
import {Button} from '@therms/atalaya'
import {UnavailableTimeBlock, UnavailableTimeChips} from '@therms/react-modules'
import {
  PendingApprovalPill,
  SectionEditButton,
  SectionShell,
} from '@src/modules/Scheduling/components/SectionShell'

interface UnavailableTimesSectionProps {
  blocks?: UnavailableTimeBlock[]
  canEdit: boolean
  /** Live (approved) blocks, shown for reference while a change is pending */
  currentBlocks?: UnavailableTimeBlock[]
  /** The region's first day of the week; the chips run from it. */
  firstDayOfWeek?: string
  note?: React.ReactNode
  onEdit: () => void
  pending?: boolean
}

export function UnavailableTimesSection({
  blocks,
  canEdit,
  currentBlocks,
  firstDayOfWeek,
  note,
  onEdit,
  pending,
}: UnavailableTimesSectionProps) {
  const hasValue = !!blocks?.length

  return (
    <SectionShell
      action={
        canEdit && (hasValue || pending) ? (
          <SectionEditButton onClick={onEdit} />
        ) : undefined
      }
      title="Unavailable Times"
    >
      {pending ? (
        <>
          <div className="mb-sm gap-xxs flex flex-col items-start">
            <span className="text-color-subtle text-xs font-semibold uppercase opacity-80">
              Current
            </span>
            <UnavailableTimeChips
              blocks={currentBlocks}
              emptyLabel="None"
              firstDayOfWeek={firstDayOfWeek}
            />
          </div>

          <div className="border-caution gap-sm p-sm flex flex-col items-start rounded-lg border">
            <div className="gap-xs flex flex-wrap items-center">
              <UnavailableTimeChips
                blocks={blocks}
                emptyLabel="None requested"
                firstDayOfWeek={firstDayOfWeek}
              />
              <PendingApprovalPill />
            </div>
            {note && <div className="text-color-subtle text-xs">{note}</div>}
          </div>
        </>
      ) : hasValue ? (
        <UnavailableTimeChips blocks={blocks} firstDayOfWeek={firstDayOfWeek} />
      ) : (
        <div className="gap-sm flex flex-col items-start">
          <span className="text-color-subtle text-sm">
            {canEdit
              ? "Not set. Add the recurring days and hours you can't work so managers schedule around you."
              : 'Not set.'}
          </span>
          {canEdit && (
            <Button onClick={onEdit} outline size="sm" variant="neutral">
              + Add unavailable time
            </Button>
          )}
        </div>
      )}
    </SectionShell>
  )
}
